/**
 * Playback state machine for the intent flow animation (Requirements 5.3, 5.4, 5.5, 11.2).
 *
 * Given the pure `Flow` (its `steps` + `sealEdges`), this hook derives the playback ROUNDS from
 * `groupParallelRounds` and, when the seal set is non-empty, appends a final seal beat as a sentinel
 * round so the UI can light the provenance seal. It then drives round playback:
 *
 * - 5.3 — While `playing` and reduced motion is NOT set, it auto-advances to the next round every
 *   `STEP_MS`, wrapping to the first round after the last.
 * - 5.4 — `play`/`pause`/`stepForward`/`stepBack`/`jump` move the active round; the index is ALWAYS
 *   clamped/wrapped into `[0, roundCount - 1]`, and `jump` clamps out-of-range input into range.
 * - 5.5 / 11.2 — Under reduced motion the auto-advance timer never starts and no packet motion runs,
 *   but `stepForward`/`stepBack`/`jump` still work and update the round instantly; the active-round
 *   highlight remains.
 *
 * Reduced motion is read from `window.matchMedia('(prefers-reduced-motion: reduce)')` via a small
 * helper, but an explicit `reducedMotion` option overrides it so tests can drive it deterministically
 * (task 9.5). Auto-advance uses `window.setInterval`/`clearInterval` so vitest fake timers can drive
 * ticks (task 9.4). Both are kept deliberately simple and deterministic.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { groupParallelRounds, type Flow, type FlowStep, type SealEdge } from "./flowData.ts";

/** Default auto-advance interval, matching the AgentDNA dashboard (`STEP_MS`). */
export const STEP_MS = 2000;

/** Read the OS reduced-motion preference. Falls back to `false` outside a browser (SSR/tests). */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export interface PlaybackOptions {
  /** Override reduced motion (defaults to the `matchMedia` result). Lets tests drive it. */
  readonly reducedMotion?: boolean;
  /** Auto-advance interval in ms (defaults to `STEP_MS`). */
  readonly stepMs?: number;
}

export interface Playback {
  /** Active round index, always within `[0, roundCount - 1]` (0 when there are no rounds). */
  readonly round: number;
  /** Step indices lit by the active round; empty for the seal beat. */
  readonly activeSteps: readonly number[];
  /** True when the active round is the final seal beat. */
  readonly sealActive: boolean;
  /** True while auto-advance is running (always false under reduced motion). */
  readonly playing: boolean;
  /** Total number of rounds, including the seal beat when present. */
  readonly roundCount: number;
  play(): void;
  pause(): void;
  stepForward(): void;
  stepBack(): void;
  /** Jump to a round; out-of-range input is clamped into `[0, roundCount - 1]`. */
  jump(round: number): void;
}

/** Sentinel marking the seal beat in the rounds list (a round carrying no step indices). */
const SEAL_ROUND: readonly number[] = [];

/**
 * Compute the playback rounds for a flow: the hop rounds from `groupParallelRounds`, plus a trailing
 * seal beat (an empty round) when the flow has a non-empty seal set.
 */
export function playbackRounds(
  steps: readonly FlowStep[],
  sealEdges: readonly SealEdge[],
): readonly (readonly number[])[] {
  const hopRounds = groupParallelRounds(steps);
  return sealEdges.length > 0 ? [...hopRounds, SEAL_ROUND] : hopRounds;
}

/**
 * React playback hook. Pass the derived `Flow`; the round grouping comes from `groupParallelRounds`
 * and the seal beat (when `sealEdges` is non-empty) is appended as a final sentinel round.
 */
export function usePlayback(flow: Flow | null, options: PlaybackOptions = {}): Playback {
  const stepMs = options.stepMs ?? STEP_MS;

  const rounds = useMemo(
    () => playbackRounds(flow?.steps ?? [], flow?.sealEdges ?? []),
    [flow],
  );
  const roundCount = rounds.length;
  const hasSeal = (flow?.sealEdges.length ?? 0) > 0;

  const [round, setRound] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Resolve reduced motion once per render; the explicit option wins over the OS preference.
  const reduced = options.reducedMotion ?? prefersReducedMotion();

  // Keep the active round in range whenever the round set shrinks/changes.
  useEffect(() => {
    setRound((r) => clampRound(r, roundCount));
  }, [roundCount]);

  // Reduced motion must never auto-advance; stop any running playback.
  useEffect(() => {
    if (reduced) setPlaying(false);
  }, [reduced]);

  // Auto-advance (5.3): only while playing, not reduced, and with at least one round. Uses
  // setInterval so fake timers can drive ticks deterministically.
  useEffect(() => {
    if (!playing || reduced || roundCount === 0) return;
    const id = window.setInterval(() => {
      setRound((r) => wrapRound(r + 1, roundCount));
    }, stepMs);
    return () => window.clearInterval(id);
  }, [playing, reduced, roundCount, stepMs]);

  const play = useCallback(() => {
    if (!reduced) setPlaying(true);
  }, [reduced]);
  const pause = useCallback(() => setPlaying(false), []);
  const stepForward = useCallback(() => {
    setPlaying(false);
    setRound((r) => wrapRound(r + 1, roundCount));
  }, [roundCount]);
  const stepBack = useCallback(() => {
    setPlaying(false);
    setRound((r) => wrapRound(r - 1, roundCount));
  }, [roundCount]);
  const jump = useCallback(
    (target: number) => {
      setPlaying(false);
      setRound(clampRound(target, roundCount));
    },
    [roundCount],
  );

  const safeRound = clampRound(round, roundCount);
  const activeRound = rounds[safeRound] ?? SEAL_ROUND;
  const sealActive = hasSeal && safeRound === roundCount - 1;

  return {
    round: safeRound,
    activeSteps: activeRound,
    sealActive,
    playing: playing && !reduced,
    roundCount,
    play,
    pause,
    stepForward,
    stepBack,
    jump,
  };
}

/** Clamp a round index into `[0, roundCount - 1]` (0 when there are no rounds). */
function clampRound(round: number, roundCount: number): number {
  if (roundCount <= 0) return 0;
  if (!Number.isFinite(round)) return 0;
  const i = Math.trunc(round);
  if (i < 0) return 0;
  if (i > roundCount - 1) return roundCount - 1;
  return i;
}

/** Wrap a round index into `[0, roundCount - 1]`, cycling past either end. */
function wrapRound(round: number, roundCount: number): number {
  if (roundCount <= 0) return 0;
  return ((round % roundCount) + roundCount) % roundCount;
}
